import { TFunction } from 'i18next'
import { formatDateTime, formatNumberWithTwoDecimals } from '../../../utils/general'
import { AgentPrintData } from './types'

export interface Row {
    label: string
    value: string | null | undefined
}

// multiselect fields
const joinNames = (items: { name: string }[]) =>
    items
        .map((i) => i.name)
        .filter(Boolean)
        .join(', ') || '—'

export const statusSection = (data: AgentPrintData, t: TFunction): Row[] => [
    { label: t('app.common.status'), value: data.propertyStatus?.name ?? '—' },
    { label: t('app.common.receiptDate'), value: data.propertyReceiptDate ? formatDateTime(data.propertyReceiptDate) : '—' },
    { label: 'ID', value: data.internalAppId != null ? String(data.internalAppId) : '—' },
]

export const profileSection = (data: AgentPrintData, t: TFunction): Row[] => [
    { label: t('app.common.service'), value: data.propertyService?.name },
    { label: t('app.common.property.type'), value: data.propertyType?.name },
    { label: t('app.common.property.subtype'), value: data.propertySubType?.name ?? '—' },
]

export const locationSection = (data: AgentPrintData, t: TFunction): Row[] => [
    { label: t('app.common.country'), value: data.country?.name },
    { label: t('app.common.county'), value: data.county?.name },
    { label: t('app.common.city'), value: data.city?.name },
    { label: t('app.common.widerArea'), value: data.widerArea?.name },
    { label: t('app.common.street'), value: [data.street, data.streetNumber].filter(Boolean).join(' ') || null },
]

export const priceSection = (data: AgentPrintData, t: TFunction): Row[] => [
    { label: t('table.header.profile.price'), value: `${formatNumberWithTwoDecimals(data.price)} €` },
    { label: t('template.priceM2'), value: data.priceM2 ? `${formatNumberWithTwoDecimals(data.priceM2)} €/m²` : '—' },
]

export const detailsSection = (data: AgentPrintData, t: TFunction, translation: { propertyTitle?: string } | undefined): Row[] => [
    { label: t('app.common.spaceArea'), value: data.area ? `${data.area} m²` : null },
    { label: t('app.common.property.orientation'), value: joinNames(data.propertyOrientation) },
    { label: t('app.common.rooms'), value: data.propertyRoomsNo?.name ?? '—' },
    { label: t('app.common.floorNumber'), value: data.floorsTotalNo != null ? String(data.floorsTotalNo) : '—' },
    { label: t('app.common.floor'), value: data.floorNo != null ? String(data.floorNo) : '—' },
    { label: t('app.common.property.constructionYear'), value: data.constructionYear != null ? String(data.constructionYear) : '—' },
    { label: t('app.common.property.adaptationYear'), value: data.adaptationYear != null ? String(data.adaptationYear) : '—' },
    { label: t('app.common.heating'), value: joinNames(data.propertyHeating) },
    { label: t('app.common.property.carpentry'), value: joinNames(data.propertyWindowsDoors) },
    { label: t('table.header.diary.insert'), value: data.propertyTransaction?.propertyRegistryInsert ?? '—' },
    { label: t('table.header.diary.cadastralMunicipality'), value: data.propertyTransaction?.propertyCadastralMunicipality ?? '—' },
    { label: t('app.common.agencyCommission'), value: data.agencyCommission?.name ?? '—' },
    { label: t('app.common.title'), value: translation?.propertyTitle ?? '—' },
]

export const agentSection = (agent: AgentPrintData['agent'], t: TFunction): Row[] => [
    { label: t('app.common.contact.nameAndSurname'), value: [agent.firstName, agent.lastName].filter(Boolean).join(' ') || '—' },
    { label: t('admin.phone'), value: agent.phoneNumber ?? '—' },
    { label: t('admin.mobilePhone'), value: agent.mobileNumber ?? '—' },
    { label: 'Email', value: agent.primaryEmail ?? '—' },
]

const getOwnerName = (o: AgentPrintData['owner'][0]): string => {
    if (o.contactType === 'company') {
        return o.contactCompany?.fullName ?? o.contactCompany?.name ?? '—'
    }
    return [o.contactPerson?.firstName, o.contactPerson?.lastName].filter(Boolean).join(' ') || '—'
}

export const ownerSection = (owners: AgentPrintData['owner'], t: TFunction): Row[][] =>
    owners.map((o) => [
        { label: `${o.contactType === 'company' ? t('app.common.company.name') : t('app.common.contact.nameAndSurname')}`, value: getOwnerName(o) },
        { label: t('admin.phone'), value: o.phone ?? '—' },
        { label: 'Email', value: o.email ?? '—' },
    ])
